/**
 * The population view's drawing constants.
 *
 * The bivariate palette is the contract's — the map-lab slice owns the single
 * copy in `ts/lib/mapReviewLayers.ts` because the same colours also paint the
 * workbench map's GeoJSON layer. The population panels import it rather than
 * keeping a second copy that could drift from the layer beside them.
 */

import {
	POPULATION_BIV,
	POPULATION_NEUTRAL_COLOR,
} from "@/lib/mapReviewLayers";

export { POPULATION_BIV };

/** The fill for a cell whose level or slope is missing: a class needs both.
 * The workbench layer's own neutral, so the rail and the sent map agree on what
 * "no class" looks like. */
export const NO_DATA_FILL = POPULATION_NEUTRAL_COLOR;

/** Class axes, in class-index order: slope class is the row, level the column. */
export const SLOPE_NAMES = ["falling", "stable", "rising"] as const;
export const LEVEL_NAMES = ["low", "medium", "high"] as const;

/** One class's fill, or the neutral no-data tint for a missing class. */
export function bivariateFill(classOfCell: number | null | undefined): string {
	if (
		classOfCell == null ||
		!Number.isInteger(classOfCell) ||
		classOfCell < 0 ||
		classOfCell > 8
	) {
		return NO_DATA_FILL;
	}
	return POPULATION_BIV[Math.floor(classOfCell / 3)][classOfCell % 3];
}

/** "<rising|stable|falling> · <high|medium|low>", the zone's name. */
export function classLabel(classOfCell: number): string {
	const slope = SLOPE_NAMES[Math.floor(classOfCell / 3)];
	const level = LEVEL_NAMES[classOfCell % 3];
	return `${slope} · ${level}`;
}

/** A break value as the legend prints it: one decimal, never a bare float. */
export function formatBreak(value: number): string {
	return Number.isFinite(value) ? value.toFixed(1) : "—";
}
